/**
 * Automatic discount price display
 * Probes Storefront carts one variant at a time (matches "add this item" pricing),
 * with a concurrency pool + session cache. Merchant compare-at always wins.
 */
(() => {
  const settings = () => (window.FoxTheme && FoxTheme.settings && FoxTheme.settings.autoDiscountPrice) || {};

  const API_VERSION = '2025-01';
  const CONCURRENCY = 6;
  // v3 stores discount titles for PLP badge display.
  const STORAGE_KEY = 'ab-auto-discount-price-v3';
  const cache = new Map();
  const inflight = new Map();
  const pending = new Map();
  let flushTimer = null;
  let flushPromise = null;
  let scanTimer = null;
  let observer;
  let activeWorkers = 0;

  const CART_CREATE = `
    mutation AutoDiscountPrice($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          lines(first: 1) {
            nodes {
              quantity
              cost {
                amountPerQuantity { amount }
                totalAmount { amount }
              }
              discountAllocations {
                __typename
                discountedAmount { amount }
                ... on CartAutomaticDiscountAllocation {
                  title
                }
              }
              merchandise {
                ... on ProductVariant { id }
              }
            }
          }
        }
        userErrors { field message }
      }
    }
  `;

  function formatMoney(cents) {
    const moneyFormat = (FoxTheme.settings && FoxTheme.settings.moneyFormat) || '${{amount}}';
    if (FoxTheme.Currency && typeof FoxTheme.Currency.formatMoney === 'function') {
      return FoxTheme.Currency.formatMoney(cents, moneyFormat);
    }
    return `$${(Number(cents) / 100).toFixed(2)}`;
  }

  function toCents(amount) {
    const n = Number(amount);
    if (!Number.isFinite(n)) return null;
    return Math.round(n * 100);
  }

  function variantGid(id) {
    const raw = String(id || '');
    if (raw.startsWith('gid://')) return raw;
    return `gid://shopify/ProductVariant/${raw}`;
  }

  function numericVariantId(gidOrId) {
    const raw = String(gidOrId || '');
    const match = raw.match(/(\d+)\s*$/);
    return match ? match[1] : raw;
  }

  function readSessionCache() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      Object.entries(parsed).forEach(([key, value]) => {
        // Only hydrate positive results; ignore stale nulls.
        if (!cache.has(key) && value && value.saleCents) cache.set(key, value);
      });
    } catch (error) {
      // Ignore private mode / quota errors.
    }
  }

  function writeSessionCache() {
    try {
      const payload = {};
      cache.forEach((value, key) => {
        if (value && value.saleCents) payload[key] = value;
      });
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      // Ignore private mode / quota errors.
    }
  }

  async function storefrontFetch(query, variables) {
    const cfg = settings();
    if (!cfg.token || !cfg.shop) return null;
    const response = await fetch(`https://${cfg.shop}/api/${API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': cfg.token,
      },
      body: JSON.stringify({ query, variables }),
    });
    if (!response.ok) throw new Error(`Storefront API ${response.status}`);
    return response.json();
  }

  function automaticDiscountInfo(line) {
    const allocations = line?.discountAllocations || [];
    let total = 0;
    let title = '';
    for (const allocation of allocations) {
      if (allocation.__typename && allocation.__typename !== 'CartAutomaticDiscountAllocation') continue;
      if (!allocation.__typename && !Object.prototype.hasOwnProperty.call(allocation, 'title')) continue;
      const amount = toCents(allocation.discountedAmount?.amount);
      if (amount && amount > 0) {
        total += amount;
        if (!title && allocation.title) title = String(allocation.title).trim();
      }
    }
    return total > 0 ? { amount: total, title } : null;
  }

  function resultFromLine(line, listPriceCents) {
    const info = automaticDiscountInfo(line);
    if (!info) return null;

    const amountPerQty = toCents(line.cost?.amountPerQuantity?.amount);
    const totalAmount = toCents(line.cost?.totalAmount?.amount);
    const listCents = Number(listPriceCents) || amountPerQty;
    const saleCents =
      totalAmount != null
        ? Math.round(totalAmount / Math.max(line.quantity || 1, 1))
        : listCents != null
          ? listCents - info.amount
          : null;

    if (saleCents == null || listCents == null || saleCents >= listCents) return null;
    return {
      listCents,
      saleCents,
      discountCents: listCents - saleCents,
      title: info.title || '',
    };
  }

  async function probeVariant(variantId, listCents) {
    const key = numericVariantId(variantId);
    if (cache.has(key)) return cache.get(key);
    if (inflight.has(key)) return inflight.get(key);

    const promise = (async () => {
      try {
        const data = await storefrontFetch(CART_CREATE, {
          input: {
            lines: [{ merchandiseId: variantGid(key), quantity: 1 }],
          },
        });
        const payload = data?.data?.cartCreate;
        if (!payload || (payload.userErrors && payload.userErrors.length)) {
          cache.set(key, null);
          return null;
        }
        const line = payload.cart?.lines?.nodes?.[0];
        const result = line ? resultFromLine(line, listCents) : null;
        cache.set(key, result);
        if (result) writeSessionCache();
        return result;
      } catch (error) {
        console.warn('[auto-discount-price]', error);
        // Don't cache hard failures as null forever — allow retry.
        return null;
      } finally {
        inflight.delete(key);
      }
    })();

    inflight.set(key, promise);
    return promise;
  }

  function enqueueFlush() {
    if (!flushPromise) {
      flushPromise = new Promise((resolve) => {
        clearTimeout(flushTimer);
        flushTimer = setTimeout(async () => {
          try {
            await flushPendingRequests();
          } finally {
            flushPromise = null;
            resolve();
          }
        }, 30);
      });
    }
    return flushPromise;
  }

  async function flushPendingRequests() {
    const queue = [...pending.entries()];
    pending.clear();
    if (!queue.length) return;

    let index = 0;
    const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
      while (index < queue.length) {
        const current = queue[index++];
        activeWorkers += 1;
        try {
          await probeVariant(current[0], current[1]);
        } finally {
          activeWorkers -= 1;
        }
      }
    });
    await Promise.all(workers);
  }

  async function fetchDiscountedPrices(requests) {
    requests.forEach((req) => {
      const key = numericVariantId(req.variantId);
      if (!key || cache.has(key) || inflight.has(key)) return;
      if (!pending.has(key)) pending.set(key, Number(req.listCents));
    });

    const needed = requests
      .map((req) => numericVariantId(req.variantId))
      .filter((key) => key && !cache.has(key));

    if (needed.length) await enqueueFlush();

    // Wait for any in-flight probes for these ids.
    await Promise.all(
      needed.map(async (key) => {
        if (inflight.has(key)) await inflight.get(key);
      })
    );

    return requests.map((req) => cache.get(numericVariantId(req.variantId)) || null);
  }

  async function fetchDiscountedPrice(variantId, listPriceCents) {
    const key = numericVariantId(variantId);
    if (cache.has(key)) return cache.get(key);
    await fetchDiscountedPrices([{ variantId: key, listCents: listPriceCents }]);
    return cache.has(key) ? cache.get(key) : null;
  }

  function applySaleUI(priceEl, listCents, saleCents, title = '') {
    if (!priceEl || saleCents >= listCents) return;

    priceEl.classList.add('f-price--on-sale');
    priceEl.classList.remove('f-price--sold-out');

    const regularRoot = priceEl.querySelector('.f-price__regular .f-price-item--regular');
    if (regularRoot) regularRoot.textContent = formatMoney(listCents);

    const saleRoot = priceEl.querySelector('.f-price__sale .f-price-item--sale');
    if (saleRoot) saleRoot.textContent = formatMoney(saleCents);

    const compareRoot = priceEl.querySelector('.f-price__sale .f-price-item--regular');
    if (compareRoot) compareRoot.innerHTML = `<s>${formatMoney(listCents)}</s>`;

    priceEl.dataset.autoDiscountApplied = 'true';
    priceEl.dataset.autoDiscountSaleCents = String(saleCents);
    if (title) priceEl.dataset.autoDiscountTitle = title;

    updatePdpBadge(priceEl, title);
  }

  function updatePdpBadge(priceEl, title = '') {
    const wrap = priceEl?.closest('[data-product-price-wrap]');
    const badge = wrap?.querySelector('[data-auto-discount-pdp-badge]');
    if (!badge) return;
    if (badge.dataset.enabled === 'false') {
      badge.hidden = true;
      badge.textContent = '';
      return;
    }
    if (title) {
      badge.textContent = title;
      badge.hidden = false;
    } else {
      badge.textContent = '';
      badge.hidden = true;
    }
  }

  function resetSaleUI(priceEl, listCents) {
    if (!priceEl) return;
    priceEl.classList.remove('f-price--on-sale');
    delete priceEl.dataset.autoDiscountApplied;
    delete priceEl.dataset.autoDiscountSaleCents;
    delete priceEl.dataset.autoDiscountTitle;

    const money = formatMoney(listCents);
    const regularRoot = priceEl.querySelector('.f-price__regular .f-price-item--regular');
    if (regularRoot) regularRoot.textContent = money;
    const saleRoot = priceEl.querySelector('.f-price__sale .f-price-item--sale');
    if (saleRoot) saleRoot.textContent = money;

    updatePdpBadge(priceEl, '');
  }

  async function processElement(priceEl, { force = false } = {}) {
    if (!priceEl) return;
    // PLP enriched cards use the badge slot instead of sale-price styling.
    if (priceEl.closest('product-card-enriched')) return;
    if (!force && priceEl.dataset.autoDiscountApplied === 'true') return;
    if (priceEl.classList.contains('f-price--on-sale') && !priceEl.hasAttribute('data-auto-discount-price')) {
      return;
    }

    const variantId = priceEl.dataset.variantId;
    const listCents = Number(priceEl.dataset.priceCents);
    if (!variantId || !Number.isFinite(listCents)) return;

    const result = await fetchDiscountedPrice(variantId, listCents);
    if (!priceEl.isConnected) return;
    if (
      priceEl.dataset.variantId !== String(variantId) &&
      priceEl.dataset.variantId !== numericVariantId(variantId)
    ) {
      return;
    }

    if (!result) {
      if (force) resetSaleUI(priceEl, listCents);
      return;
    }

    applySaleUI(priceEl, result.listCents || listCents, result.saleCents, result.title || '');
  }

  function applyBadgeUI(badgeEl, result, { fallbackVisible = false, fallbackText = '' } = {}) {
    if (!badgeEl) return;
    const defaultText = badgeEl.dataset.defaultText || fallbackText || 'BUY MORE & SAVE';

    if (result?.title) {
      badgeEl.textContent = result.title;
      badgeEl.hidden = false;
      badgeEl.dataset.autoDiscountActive = 'true';
      return;
    }

    badgeEl.textContent = defaultText;
    badgeEl.hidden = !fallbackVisible;
    delete badgeEl.dataset.autoDiscountActive;
  }

  async function processBadge(badgeEl, options = {}) {
    if (!badgeEl || !badgeEl.hasAttribute('data-auto-discount-badge')) return;

    const variantId = options.variantId ?? badgeEl.dataset.variantId;
    const listCents = Number(options.priceCents ?? badgeEl.dataset.priceCents);
    if (!variantId || !Number.isFinite(listCents)) return;

    badgeEl.dataset.variantId = String(variantId);
    badgeEl.dataset.priceCents = String(listCents);

    const result = await fetchDiscountedPrice(variantId, listCents);
    if (!badgeEl.isConnected) return;
    if (
      badgeEl.dataset.variantId !== String(variantId) &&
      badgeEl.dataset.variantId !== numericVariantId(variantId)
    ) {
      return;
    }

    applyBadgeUI(badgeEl, result, options);
  }

  async function processBadges(elements) {
    const targets = elements.filter(Boolean);
    if (!targets.length) return;

    await fetchDiscountedPrices(
      targets.map((el) => ({
        variantId: el.dataset.variantId,
        listCents: Number(el.dataset.priceCents),
      }))
    );

    targets.forEach((badgeEl) => {
      if (!badgeEl.isConnected) return;
      const result = cache.get(numericVariantId(badgeEl.dataset.variantId));
      const fallbackVisible = badgeEl.dataset.fallbackVisible === 'true';
      applyBadgeUI(badgeEl, result, {
        fallbackVisible,
        fallbackText: badgeEl.dataset.defaultText || '',
      });
    });
  }

  async function processElements(elements, { force = false } = {}) {
    const targets = elements.filter(Boolean).filter((el) => !el.closest('product-card-enriched'));
    if (!targets.length) return;

    const requests = targets.map((el) => ({
      variantId: el.dataset.variantId,
      listCents: Number(el.dataset.priceCents),
    }));

    await fetchDiscountedPrices(requests);

    targets.forEach((priceEl) => {
      if (!priceEl.isConnected) return;
      if (!force && priceEl.dataset.autoDiscountApplied === 'true') return;

      const variantId = priceEl.dataset.variantId;
      const listCents = Number(priceEl.dataset.priceCents);
      const result = cache.get(numericVariantId(variantId));
      if (!result) {
        if (force) resetSaleUI(priceEl, listCents);
        return;
      }
      applySaleUI(priceEl, result.listCents || listCents, result.saleCents, result.title || '');
    });
  }

  function collectTargets(root = document) {
    return [...root.querySelectorAll('[data-auto-discount-price]')].filter(
      (el) => el.dataset.autoDiscountApplied !== 'true' && !el.closest('product-card-enriched')
    );
  }

  function collectBadgeTargets(root = document) {
    return [...root.querySelectorAll('[data-auto-discount-badge]')].filter(
      (el) => el.dataset.autoDiscountActive !== 'true'
    );
  }

  function scheduleScan(root) {
    if (!settings().enabled) return;
    clearTimeout(scanTimer);
    scanTimer = setTimeout(() => {
      const scope = root || document;
      const targets = collectTargets(scope);
      const badges = collectBadgeTargets(scope);

      const runVisible = (els, handler) => {
        if (!els.length) return;
        if ('IntersectionObserver' in window) {
          const pendingEls = new Set(els);
          const immediate = [];
          const io = new IntersectionObserver(
            (entries) => {
              const visible = [];
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                io.unobserve(entry.target);
                if (pendingEls.has(entry.target)) {
                  pendingEls.delete(entry.target);
                  visible.push(entry.target);
                }
              });
              if (visible.length) handler(visible);
            },
            { rootMargin: '120px 0px', threshold: 0.01 }
          );

          els.forEach((el) => {
            const rect = el.getBoundingClientRect();
            const inView = rect.bottom >= 0 && rect.top <= (window.innerHeight || 0) + 120;
            if (inView) immediate.push(el);
            else io.observe(el);
          });

          if (immediate.length) {
            immediate.forEach((el) => pendingEls.delete(el));
            handler(immediate);
          }
        } else {
          handler(els);
        }
      };

      runVisible(targets, processElements);
      runVisible(badges, (els) => {
        els.forEach((el) => {
          if (!el.dataset.fallbackVisible) {
            el.dataset.fallbackVisible = el.hidden ? 'false' : 'true';
          }
        });
        processBadges(els);
      });
    }, 50);
  }

  async function refreshElement(priceEl, { variantId, priceCents, prefetchVariants } = {}) {
    if (!settings().enabled || !priceEl) return;
    if (!priceEl.hasAttribute('data-auto-discount-price')) return;
    if (priceEl.closest('product-card-enriched')) return;

    if (variantId != null) priceEl.dataset.variantId = String(variantId);
    if (priceCents != null) priceEl.dataset.priceCents = String(priceCents);

    delete priceEl.dataset.autoDiscountApplied;
    delete priceEl.dataset.autoDiscountSaleCents;

    const prefetch = Array.isArray(prefetchVariants)
      ? prefetchVariants
          .filter((item) => item && item.variantId != null && item.priceCents != null)
          .map((item) => ({
            variantId: item.variantId,
            listCents: Number(item.priceCents),
          }))
      : [];

    if (prefetch.length) {
      fetchDiscountedPrices(prefetch);
    }

    await processElement(priceEl, { force: true });
  }

  async function refreshBadge(
    badgeEl,
    { variantId, priceCents, prefetchVariants, fallbackVisible = false, fallbackText = '' } = {}
  ) {
    if (!settings().enabled || !badgeEl) return;
    if (!badgeEl.hasAttribute('data-auto-discount-badge')) return;

    if (variantId != null) badgeEl.dataset.variantId = String(variantId);
    if (priceCents != null) badgeEl.dataset.priceCents = String(priceCents);
    badgeEl.dataset.fallbackVisible = fallbackVisible ? 'true' : 'false';
    if (fallbackText) badgeEl.dataset.defaultText = fallbackText;

    const prefetch = Array.isArray(prefetchVariants)
      ? prefetchVariants
          .filter((item) => item && item.variantId != null && item.priceCents != null)
          .map((item) => ({
            variantId: item.variantId,
            listCents: Number(item.priceCents),
          }))
      : [];

    if (prefetch.length) fetchDiscountedPrices(prefetch);

    await processBadge(badgeEl, {
      variantId,
      priceCents,
      fallbackVisible,
      fallbackText: fallbackText || badgeEl.dataset.defaultText || '',
    });
  }

  async function prefetch(requests) {
    if (!settings().enabled || !Array.isArray(requests) || !requests.length) return;
    await fetchDiscountedPrices(
      requests.map((item) => ({
        variantId: item.variantId,
        listCents: Number(item.listCents ?? item.priceCents),
      }))
    );
  }

  function refreshFromVariant(variant) {
    if (!variant || !variant.id) return;
    if (variant.compare_at_price && variant.compare_at_price > variant.price) return;

    document
      .querySelectorAll(
        'product-info [data-auto-discount-price], sticky-atc-bar [data-auto-discount-price], sticky-atc-bar-minimalist [data-auto-discount-price]'
      )
      .forEach((el) => {
        refreshElement(el, { variantId: variant.id, priceCents: variant.price });
      });
  }

  function onVariantChange(event) {
    const variant = event?.data?.variant;
    if (!variant) return;
    scheduleScan(document);
    setTimeout(() => refreshFromVariant(variant), 0);
  }

  function init() {
    const cfg = settings();
    if (!cfg.enabled || !cfg.token || !cfg.shop) return;

    // Drop legacy caches without discount titles / false nulls.
    try {
      sessionStorage.removeItem('ab-auto-discount-price-v1');
      sessionStorage.removeItem('ab-auto-discount-price-v2');
    } catch (error) {
      // ignore
    }

    readSessionCache();
    scheduleScan(document);

    if (window.FoxTheme?.pubsub?.subscribe) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, onVariantChange);
    }

    document.addEventListener('variant:changed', (event) => {
      const variant = event?.detail?.variant;
      if (variant) {
        scheduleScan(document);
        setTimeout(() => refreshFromVariant(variant), 0);
      }
    });

    if ('MutationObserver' in window) {
      observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (![...mutation.addedNodes].some((n) => n.nodeType === 1)) continue;
          scheduleScan(document);
          break;
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  window.FoxTheme = window.FoxTheme || {};
  FoxTheme.autoDiscountPrice = {
    refreshElement,
    refreshBadge,
    prefetch,
    processElements,
    getCached(variantId) {
      return cache.get(numericVariantId(variantId)) || null;
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
